import { useStytchMemberSession, useStytchB2BClient } from '@stytch/react/b2b';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AppProvider } from './AppContext';
import { useCurrentUser } from './providers/currentUser/CurrentUserProvider';
import { UnsavedChangesProvider } from './hooks/useUnsavedChanges';
import ChooseOrganization from './components/layout/ChooseOrganization';
import Frame from './components/layout/Frame';
import Authorize from './components/stytch/Authorize';
import Login from './components/stytch/Login';
import ResetPassword from './components/stytch/ResetPassword';
//import SignUp from './components/stytch/SignUp';
import Invite from './components/stytch/Invite';

//const RootRedirect = () => {
//  const app = useAppContext();
//  const lastOrg = localStorage.getItem("lastOrgSlug");

//  if (lastOrg) {
//    return <Navigate to={`/${lastOrg}`} replace />;
//  }

//  return <Navigate to="/login" replace />;
//};

// The per-route auth gate, previously inline on the /:orgSlug/* route. Pulled into a component so it can run
// as a data-router route element (createBrowserRouter takes elements, not conditional expressions with
// outer-scope state). Renders inside all of main.tsx's providers, so the Stytch hooks resolve as before.
//
// A Stytch session is NOT sufficient to render the app. Stytch only says the credentials were good; whether
// the account is still allowed in is ours to answer, and disabled and deleted users authenticate perfectly
// well. This used to gate on the session alone, so the instant sign-in succeeded the whole authenticated
// shell mounted with no current user: the name rendered empty, every permission check failed closed and
// showed "Access denied", and only then did CurrentUserProvider revoke the session and bounce back to Login.
// A disabled user was blocked, but only after being shown the inside of the app.
//
// So the shell waits for OUR confirmation, not Stytch's. No user, no Frame — whatever the reason.
const OrgGate = (): ReactElement => {
    const { session, isInitialized } = useStytchMemberSession();
    const { user, isLoading } = useCurrentUser();

    // Signed out, or the session has just been revoked by a refusal.
    if (!isInitialized || session === null) return <Login />;

    // Authenticated, not yet confirmed. Brief on a normal sign-in; it is the window that used to leak the
    // shell. Deliberately not <Frame /> with a spinner inside — the point is that nothing behind the gate
    // renders for an account we have not vouched for.
    if (isLoading) return <SignInProgress />;

    // Confirmed refusal (session revoke is in flight) or a fetch that failed outright. Either way we cannot
    // vouch for this session, so back to the sign-in screen — which shows the reason when there is one.
    if (!user) return <Login />;

    return <Frame />;
};

// Deliberately bare: this is on screen for a moment during every sign-in, and anything more elaborate reads
// as a page rather than a transition.
const SignInProgress = (): ReactElement => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
        <CircularProgress />
    </Box>
);

// Data router (createBrowserRouter) rather than <BrowserRouter> — required for useBlocker, which powers the
// unsaved-changes navigation guard on the Brand/Location detail screens. Route set is unchanged from the old
// <Routes> tree. Descendant <Routes> (e.g. Frame's) keep working underneath a data router.
const router = createBrowserRouter([
    { path: '/oauth/authorize', element: <Authorize /> },
    { path: '/', element: <ChooseOrganization /> },
    { path: '/:orgSlug/*', element: <OrgGate /> },
    { path: '/invite', element: <Invite /> },
    { path: '/resetpassword/*', element: <ResetPassword /> },
]);

const App = (): ReactElement => {
    const { session, isInitialized } = useStytchMemberSession();
    const stytch = useStytchB2BClient();
  
    useEffect(() => {
        const SESSION_DURATION_MINUTES = parseInt(import.meta.env.VITE_STYTCH_SESSION_DURATION);
        const REFRESH_EVERY_MS = 1 * 60 * 1000;
        const IDLE_TIMEOUT_MS = 2 * 60 * 1000;

        if (!isInitialized || !session) return;

        let lastActivity = Date.now();
        //console.log("lastActivity initialized at", new Date(lastActivity).toISOString());

        const markActive = () => {
            lastActivity = Date.now();
            //console.log("lastActivity updated at", new Date(lastActivity).toISOString());
        };

        const refreshSession = async () => {           
            const isIdle = Date.now() - lastActivity > IDLE_TIMEOUT_MS;

            if (isIdle) {
                console.log("User is idle, skipping session refresh");
                return;
            }

            if (document.visibilityState !== "visible") return;

            try {
                //console.log("Refreshing Stytch session");
                await stytch.session.authenticate({
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });
            } catch (err) {
                console.error("Stytch session refresh failed", err);
            }
        };

        window.addEventListener("mousemove", markActive);
        window.addEventListener("keydown", markActive);
        window.addEventListener("click", markActive);
        window.addEventListener("touchstart", markActive);
        window.addEventListener("scroll", markActive);

        refreshSession();

        const interval = window.setInterval(refreshSession, REFRESH_EVERY_MS);

        return () => {
            window.clearInterval(interval);
            window.removeEventListener("mousemove", markActive);
            window.removeEventListener("keydown", markActive);
            window.removeEventListener("click", markActive);
            window.removeEventListener("touchstart", markActive);
            window.removeEventListener("scroll", markActive);
        };
    }, [isInitialized, session, stytch]);

    return (
        <AppProvider>
            {/* Sits outside the router on purpose: the scope selector lives in the app bar and needs to know
                a page is dirty in order to ask before switching org/brand/location, which is not a route
                change and so never reaches the router's blocker. */}
            <UnsavedChangesProvider>
                <main>
                    <div className="container">
                        <RouterProvider router={router} />
                    </div>
                </main>
            </UnsavedChangesProvider>
        </AppProvider>
    );
};

export default App;